import React from "react";

// ============================= Types =============================

export type HistoricoItem = {
  id?: number | string | null;
  produto_id?: number | string | null;
  movimentacao_id?: number | string | null;
  item_id?: number | string | null;

  tipo?:
    | "entrada"
    | "saida"
    | "perda"
    | "venda"
    | "consumo"
    | "retorno"
    | "ajuste"
    | "standby"
    | "transferir"
    | string
    | null;

  tipo_full?: string | null;
  codigo_movimentacao?: string | null;
  tipo_codigo?: string | null;
  tipo_descricao?: string | null;

  custo_unitario?: string | number | null;
  quantidade?: number | string | null;
  data_movimentacao?: string | null;
  criado_em?: string | null;

  responsavel_id?: number | string | null;
  responsavel_nome?: string | null;

  moeda?: string | null;

  os_numero?: string | null;
  proposta_venda?: string | null;
  motivo?: string | null;
  origem_mov_id?: number | string | null;
};

export type HistoricoWidgetProps = {
  items: HistoricoItem[];
  title?: string;
  loading?: boolean;
  error?: string | null;
  formatCurrency?: (
    v: string | number | null | undefined,
    moeda?: string | null
  ) => string;
  formatDateTime?: (iso: string | null | undefined) => string;
  onRowClick?: (item: HistoricoItem) => void;
};

// ======================== Default formatters ======================

const defaultFormatCurrency = (
  v: string | number | null | undefined,
  moeda?: string | null
) => {
  if (v === null || v === undefined || v === "") return "—";

  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));

  if (!Number.isFinite(n)) return "—";

  const cur =
    moeda === "USD" || moeda === "EUR" || moeda === "BRL" ? moeda : "BRL";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: cur,
  }).format(n);
};

const defaultFormatDateTime = (iso: string | null | undefined) => {
  if (!iso) return "—";

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) return "—";

  const data = d.toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const hora = d.toLocaleTimeString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return `${data} - ${hora}`;
};

// ============================= Layout =============================

const COLUMNS = ["Responsável", "Q.", "Tipo", "Custo", "Data"] as const;

const gridTemplate = "2fr 0.8fr 1.3fr 1fr 1.5fr";

const COLORS = {
  entradaBg: "#EAF7EA",
  entradaAccent: "#6EBB6E",

  saidaBg: "#FDEAEA",
  saidaAccent: "#D66",

  retornoBg: "#E7F1FF",
  retornoAccent: "#4A90E2",

  perdaBg: "#FFF0E6",
  perdaAccent: "#E58A35",

  ajusteBg: "#F1ECFF",
  ajusteAccent: "#8A6DE9",

  neutroBg: "#F7F7F7",
  neutroAccent: "#9A9A9A",

  header: "#09363f",
  border: "#d8d8d8",
  borderStrong: "#b8b8b8",
  text: "#111",
  mutedText: "#333",
};

type MovimentoKind =
  | "entrada"
  | "saida"
  | "saida_standby"
  | "retorno"
  | "perda"
  | "venda"
  | "consumo"
  | "ajuste"
  | "neutro";

type MovimentoPresentation = {
  kind: MovimentoKind;
  label: string;
  effect: "+x" | "-x" | "0" | "ajuste";
  bg: string;
  accent: string;
};

const styles: { [k: string]: React.CSSProperties } = {
  wrapper: {
    width: "100%",
    marginTop: 30,
    fontFamily: "Inter, sans-serif",
    boxSizing: "border-box",
    background: "#fff",
    borderRadius: 0,
    overflow: "hidden",
    border: `1px solid ${COLORS.borderStrong}`,
  },
  top: {
    height: 35,
    backgroundColor: COLORS.header,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 14,
    borderBottom: `1px solid ${COLORS.borderStrong}`,
  },
  legendWrap: {
    padding: "10px 12px",
    display: "flex",
    flexWrap: "wrap",
    gap: "8px 14px",
    alignItems: "center",
    borderBottom: `1px solid ${COLORS.border}`,
    background: "#fafafa",
    fontSize: 12,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: COLORS.mutedText,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  swatch: {
    width: 14,
    height: 14,
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.12)",
    boxSizing: "border-box",
  },
  formula: {
    flexBasis: "100%",
    fontSize: 12,
    color: COLORS.mutedText,
    opacity: 0.88,
    marginTop: 2,
  },
  headerRow: {
    height: 35,
    backgroundColor: COLORS.header,
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "center",
    color: "#fff",
    fontWeight: 600,
    fontSize: 13,
    boxSizing: "border-box",
    borderBottom: `1px solid ${COLORS.borderStrong}`,
  },
  headerCell: {
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    boxSizing: "border-box",
    borderRight: `1px solid rgba(255,255,255,0.18)`,
  },
  body: {
    backgroundColor: "#fff",
    display: "grid",
    gridTemplateColumns: gridTemplate,
    alignItems: "stretch",
    fontSize: 13,
    boxSizing: "border-box",
  },
  bodyCell: {
    padding: "10px 12px",
    minHeight: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: COLORS.text,
    boxSizing: "border-box",
    borderRight: `1px solid ${COLORS.border}`,
  },
  tipoText: {
    color: "#000",
    fontWeight: 500,
    fontSize: 13,
    lineHeight: 1.2,
  },
  error: {
    padding: 12,
    color: "#c0362c",
    fontSize: 13,
    borderTop: `1px solid ${COLORS.border}`,
  },
};

// ============================= Helpers =============================

function norm(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function upper(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toUpperCase();
}

function hasOrigemMovId(item: HistoricoItem): boolean {
  const v = item.origem_mov_id;

  return (
    v !== null &&
    v !== undefined &&
    String(v).trim() !== "" &&
    String(v).trim() !== "0"
  );
}

function getSearchBase(item: HistoricoItem) {
  const tipo = norm(item.tipo);
  const tipoFull = norm(item.tipo_full);
  const codigo = upper(item.codigo_movimentacao);
  const tipoCodigo = upper(item.tipo_codigo);
  const descricao = norm(item.tipo_descricao);

  const text = `${tipo} ${tipoFull} ${codigo.toLowerCase()} ${tipoCodigo.toLowerCase()} ${descricao}`;

  return {
    tipo,
    tipoFull,
    codigo,
    tipoCodigo,
    descricao,
    text,
  };
}

function classifyMovement(item: HistoricoItem): MovimentoPresentation {
  const { tipo, tipoFull, codigo, tipoCodigo, text } = getSearchBase(item);

  const isRetorno =
    text.includes("retorno") ||
    codigo.includes("RETORNO") ||
    tipoCodigo.includes("RETORNO") ||
    codigo === "RET-ESTOQUE" ||
    tipoCodigo === "RET-ESTOQUE";

  if (isRetorno) {
    return {
      kind: "retorno",
      label: "Retorno",
      effect: "+x",
      bg: COLORS.retornoBg,
      accent: COLORS.retornoAccent,
    };
  }

  const isEntrada =
    codigo.startsWith("ENT") ||
    tipoCodigo.startsWith("ENT") ||
    tipo.includes("entrada") ||
    tipoFull.includes("entrada");

  if (isEntrada) {
    return {
      kind: "entrada",
      label: "Entrada",
      effect: "+x",
      bg: COLORS.entradaBg,
      accent: COLORS.entradaAccent,
    };
  }

  const isAjuste =
    codigo.includes("AJUSTE") ||
    tipoCodigo.includes("AJUSTE") ||
    tipo.includes("ajuste") ||
    tipoFull.includes("ajuste");

  if (isAjuste) {
    return {
      kind: "ajuste",
      label:
        text.includes("preco") || text.includes("preço")
          ? "Ajuste de Preço"
          : "Ajuste",
      effect: "ajuste",
      bg: COLORS.ajusteBg,
      accent: COLORS.ajusteAccent,
    };
  }

  const isPerda =
    codigo === "BAIXA-PERDA" ||
    codigo === "SAI-PERDA" ||
    tipoCodigo === "BAIXA-PERDA" ||
    tipoCodigo === "SAI-PERDA" ||
    tipo.includes("perda") ||
    tipoFull.includes("perda");

  if (isPerda) {
    return {
      kind: "perda",
      label: "Perda",
      effect: "-x",
      bg: COLORS.perdaBg,
      accent: COLORS.perdaAccent,
    };
  }

  const isConsumo =
    codigo === "STB-CONSUMO" ||
    codigo === "SAI-CONSUMO" ||
    codigo === "CONS-INTERNO" ||
    tipoCodigo === "STB-CONSUMO" ||
    tipoCodigo === "SAI-CONSUMO" ||
    tipoCodigo === "CONS-INTERNO" ||
    tipo.includes("consumo") ||
    tipoFull.includes("consumo");

  if (isConsumo) {
    return {
      kind: "consumo",
      label: "Consumo",
      effect: "-x",
      bg: COLORS.saidaBg,
      accent: COLORS.saidaAccent,
    };
  }

  const isVenda =
    codigo === "STB-VENDA" ||
    codigo === "STB-VENDA-SAIDA" ||
    codigo === "SAI-VENDA" ||
    tipoCodigo === "STB-VENDA" ||
    tipoCodigo === "STB-VENDA-SAIDA" ||
    tipoCodigo === "SAI-VENDA" ||
    tipo.includes("venda") ||
    tipoFull.includes("venda");

  if (isVenda) {
    const isSaidaVenda =
      codigo.includes("SAIDA") ||
      tipoFull.includes("saida") ||
      hasOrigemMovId(item);

    return {
      kind: isSaidaVenda ? "saida_standby" : "venda",
      label: isSaidaVenda ? "Saída Venda" : "Venda",
      effect: "-x",
      bg: COLORS.saidaBg,
      accent: COLORS.saidaAccent,
    };
  }

  const isServico =
    codigo === "STB-SERVICO" ||
    codigo === "STB-SERVICO-SAIDA" ||
    codigo === "SAI-SERVICO" ||
    tipoCodigo === "STB-SERVICO" ||
    tipoCodigo === "STB-SERVICO-SAIDA" ||
    tipoCodigo === "SAI-SERVICO" ||
    tipo.includes("servico") ||
    tipo.includes("serviço") ||
    tipoFull.includes("servico") ||
    tipoFull.includes("serviço");

  if (isServico) {
    const isSaidaStandby =
      codigo.includes("SAIDA") ||
      tipoFull.includes("saida") ||
      hasOrigemMovId(item);

    return {
      kind: isSaidaStandby ? "saida_standby" : "saida",
      label: isSaidaStandby ? "Saída Standby" : "Saída Serviço",
      effect: "-x",
      bg: COLORS.saidaBg,
      accent: COLORS.saidaAccent,
    };
  }

  const isTransferencia =
    tipo.includes("transfer") ||
    tipoFull.includes("transfer") ||
    codigo.includes("TRANSF") ||
    tipoCodigo.includes("TRANSF");

  if (isTransferencia) {
    return {
      kind: "neutro",
      label: "Transferência",
      effect: "0",
      bg: COLORS.neutroBg,
      accent: COLORS.neutroAccent,
    };
  }

  const isStandby =
    tipo.includes("standby") ||
    tipoFull.includes("standby") ||
    codigo.includes("STB") ||
    tipoCodigo.includes("STB");

  if (isStandby) {
    return {
      kind: "saida",
      label: "Standby",
      effect: "-x",
      bg: COLORS.saidaBg,
      accent: COLORS.saidaAccent,
    };
  }

  return {
    kind: "neutro",
    label: item.tipo ? String(item.tipo) : "Movimentação",
    effect: "0",
    bg: COLORS.neutroBg,
    accent: COLORS.neutroAccent,
  };
}

function formatQuantidade(value: HistoricoItem["quantidade"]): string {
  if (value === null || value === undefined || value === "") return "—";

  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  if (!Number.isFinite(n)) return String(value);

  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 4,
  }).format(n);
}

function getResponsavel(item: HistoricoItem): string {
  return String(item.responsavel_nome || item.responsavel_id || "—");
}

function getDate(item: HistoricoItem): string | null {
  return item.data_movimentacao || item.criado_em || null;
}

function getStableKey(item: HistoricoItem, index: number): string {
  return String(
    item.item_id ||
      item.id ||
      item.movimentacao_id ||
      `${item.codigo_movimentacao || "mov"}-${index}`
  );
}

// =========================== Component ============================

const HistoricoWidget: React.FC<HistoricoWidgetProps> = ({
  items,
  title = "Histórico de Movimentações do Produto",
  loading = false,
  error = null,
  formatCurrency = defaultFormatCurrency,
  formatDateTime = defaultFormatDateTime,
  onRowClick,
}) => {
  const safeItems = Array.isArray(items) ? items : [];
  const showEmpty = !loading && safeItems.length === 0;

  return (
    <div style={styles.wrapper}>
      <div style={styles.top}>{title}</div>

      <div style={styles.legendWrap}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: COLORS.entradaBg }} />
          <span>
            <strong>Entrada</strong>: soma ao estoque.
          </span>
        </div>

        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: COLORS.retornoBg }} />
          <span>
            <strong>Retorno</strong>: retorna item ao estoque.
          </span>
        </div>

        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: COLORS.saidaBg }} />
          <span>
            <strong>Saída / Venda / Consumo / Standby</strong>: baixa do
            estoque.
          </span>
        </div>

        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: COLORS.perdaBg }} />
          <span>
            <strong>Perda</strong>: baixa por perda.
          </span>
        </div>

        <div style={styles.legendItem}>
          <span style={{ ...styles.swatch, background: COLORS.ajusteBg }} />
          <span>
            <strong>Ajuste</strong>: ajuste operacional ou de preço.
          </span>
        </div>

        <div style={styles.formula}></div>
      </div>

      <div style={styles.headerRow}>
        {COLUMNS.map((label, idx) => (
          <div
            key={label}
            style={{
              ...styles.headerCell,
              borderRight:
                idx === COLUMNS.length - 1
                  ? "none"
                  : "1px solid rgba(255,255,255,0.18)",
            }}
          >
            {label}
          </div>
        ))}
      </div>

      <div style={styles.body}>
        {loading &&
          Array.from({ length: COLUMNS.length }).map((_, idx) => (
            <div
              key={`loading-${idx}`}
              style={{
                ...styles.bodyCell,
                borderRight:
                  idx === COLUMNS.length - 1
                    ? "none"
                    : `1px solid ${COLORS.border}`,
              }}
            >
              {idx === 0 ? "Carregando..." : "—"}
            </div>
          ))}

        {showEmpty &&
          Array.from({ length: COLUMNS.length }).map((_, idx) => (
            <div
              key={`empty-${idx}`}
              style={{
                ...styles.bodyCell,
                borderRight:
                  idx === COLUMNS.length - 1
                    ? "none"
                    : `1px solid ${COLORS.border}`,
              }}
            >
              {idx === 0 ? "Sem movimentações" : "—"}
            </div>
          ))}

        {!loading &&
          safeItems.map((item, rowIdx) => {
            const presentation = classifyMovement(item);

            const cells: React.ReactNode[] = [
              getResponsavel(item),
              formatQuantidade(item.quantidade),
              <span style={styles.tipoText}>{presentation.label}</span>,
              formatCurrency(item.custo_unitario ?? null, item.moeda ?? null),
              formatDateTime(getDate(item)),
            ];

            return cells.map((val, colIdx) => (
              <div
                key={`${getStableKey(item, rowIdx)}-${colIdx}`}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                title={
                  colIdx === 2
                    ? [
                        item.codigo_movimentacao
                          ? `Código: ${item.codigo_movimentacao}`
                          : null,
                        item.tipo_full ? `Tipo: ${item.tipo_full}` : null,
                        item.tipo_descricao
                          ? `Descrição: ${item.tipo_descricao}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" | ")
                    : undefined
                }
                style={{
                  ...styles.bodyCell,
                  background: presentation.bg,
                  borderRight:
                    colIdx === COLUMNS.length - 1
                      ? "none"
                      : `1px solid ${COLORS.border}`,
                  borderTop:
                    rowIdx === 0 ? "none" : `1px solid ${COLORS.border}`,
                  borderLeft:
                    colIdx === 0
                      ? `4px solid ${presentation.accent}`
                      : undefined,
                  cursor: onRowClick ? "pointer" : "default",
                }}
              >
                {val}
              </div>
            ));
          })}
      </div>

      {error && (
        <div style={styles.error}>Erro ao carregar histórico: {error}</div>
      )}
    </div>
  );
};

export default HistoricoWidget;
